import type { FC } from 'react';
import { TbPlus } from 'react-icons/tb';

import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownButtonSelect, {
    type DropdownButtonOption,
} from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { getWorkspaceConceptIconType, WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';

export type LifecycleAddItemType = 'category' | 'task' | 'task-group';

interface LifecycleAddDropdownProps {
    disabled?: boolean;
    onAdd: (itemType: LifecycleAddItemType) => void;
}

const LifecycleCategoryIcon = getWorkspaceConceptIconType('lifecycleCategory');
const LifecycleTaskGroupIcon = getWorkspaceConceptIconType('lifecycleTaskGroup');

const options: Array<DropdownButtonOption<LifecycleAddItemType>> = [
    {
        description: 'Create a top-level phase for Task Groups and their Tasks.',
        icon: <LifecycleCategoryIcon />,
        label: 'Task Category',
        value: 'category',
    },
    {
        description: 'Organize a related collection of Tasks inside a category.',
        icon: <LifecycleTaskGroupIcon />,
        label: 'Task Group',
        value: 'task-group',
    },
    {
        description: 'Add an actionable Task to a Task Group in this Lifecycle.',
        icon: <WorkspaceResourceGlyph resource='task' />,
        label: 'Task',
        value: 'task',
    },
];

const LifecycleAddDropdown: FC<LifecycleAddDropdownProps> = ({ disabled = false, onAdd }) => (
    <DropdownButtonSelect
        disabled={disabled}
        footer={<LearnMoreFooter topic='Lifecycle hierarchy' variant='dropdown' />}
        header={<DropdownMenuLabel icon={<TbPlus />}>Add to Lifecycle</DropdownMenuLabel>}
        matchTriggerWidth={false}
        menuMinWidth='20rem'
        options={options}
        placement='bottomRight'
        onChange={(itemType) => {
            if (itemType) {
                onAdd(itemType);
            }
        }}
    >
        <DropdownButton aria-label='Add to Lifecycle' disabled={disabled} icon={<TbPlus />} size='small'>
            Add
        </DropdownButton>
    </DropdownButtonSelect>
);

export default LifecycleAddDropdown;
